import React, { ReactNode, useCallback, useContext, createContext, useRef } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';

import { Automation } from '@/src/components/project/Inbox2/types/Automation';

import { TreatmentEvents } from '../../../../store/fileEntryViewer.treatment.store';
import { AutomatedDraft, Draft } from '../types/draft';
import { FormTypeOption, Treatment } from '../types/treatment';

const FILE_AUTOMATION_LOCAL_STORAGE_KEY = 'file-automation';

type InitializeDraftAutomationDTO = {
  fileId: keyof Automation;
  draft: Draft | AutomatedDraft;
  form: FormTypeOption;
};

const removeAndGetUpdatedAutomation = ({
  automation,
  fileId,
}: {
  automation: Automation;
  fileId: keyof Automation;
}) => {
  const newAutomation = { ...automation };

  localStorage.removeItem(`${FILE_AUTOMATION_LOCAL_STORAGE_KEY}-${fileId}`);

  if (newAutomation[fileId]) delete newAutomation[fileId];

  return { automation: newAutomation };
};

export type TreatmentsContextProviderProps = {
  fileId: number;
  children: ReactNode;
  isViewInQuickbooksEnabled?: boolean;
  isViewable?: boolean;
  treatmentEvents?: TreatmentEvents;
};

export type TreatmentZustandStore = {
  automation: Automation;
  form: FormTypeOption;
  treatment: Treatment;
  draft: Draft | AutomatedDraft;
  setForm: (form: FormTypeOption) => void;
  setTreatment: (treatment: Treatment) => void;
  setDraft: (draft: Draft | AutomatedDraft) => void;
  onUpdateDraft: (draft: Draft | AutomatedDraft) => void;
  addToAutomation: (automation: Automation) => void;
  removeFromAutomation: (fileId: keyof Automation) => void;
  initializeDraftFromAutomation: (props: InitializeDraftAutomationDTO) => void;
  onReset: () => void;
  onDiscardDetectedVendor: () => void;
};

export type TreatmentsStore = TreatmentZustandStore & {
  isViewable: boolean;
  isViewInQuickbooksEnabled: boolean;
  onPublished: () => void;
  onDraftAction: () => void;
};

const createTreatmentStore = ({ fileId, automation }) => {
  const DEFAULT_PROPS = {
    automation: automation ? JSON.parse(automation) : null,
  };

  return createStore<TreatmentZustandStore>()((set) => ({
    ...DEFAULT_PROPS,
    form: null,
    treatment: null,
    draft: null,
    setForm: (form) => set({ form, treatment: null }),
    setTreatment: (treatment) => set({ treatment, form: null, draft: null }),
    setDraft: (draft) => set({ draft, treatment: null, form: null }),
    onUpdateDraft: (draft) => set({ draft }),
    onDiscardDetectedVendor: () =>
      set((state) => {
        return {
          draft: {
            ...(state.draft as AutomatedDraft),
            payload: {
              ...(state.draft as AutomatedDraft).payload,
              detectedVendor: null,
            },
          },
        };
      }),
    addToAutomation: (automation) =>
      set((state) => {
        localStorage.setItem(
          `${FILE_AUTOMATION_LOCAL_STORAGE_KEY}-${fileId}`,
          JSON.stringify(automation)
        );

        return { automation: { ...state.automation, ...automation } };
      }),
    removeFromAutomation: (fileId) =>
      set((state) => {
        return removeAndGetUpdatedAutomation({ automation: state.automation, fileId });
      }),
    initializeDraftFromAutomation: ({ draft, form, fileId }) =>
      set((state) => {
        return {
          draft,
          form,
          automation: removeAndGetUpdatedAutomation({ automation: state.automation, fileId }),
        };
      }),
    onReset: () => {
      set({ treatment: null, form: null, draft: null });
    },
  }));
};

export const TreatmentsContext = createContext<TreatmentsStore>(undefined);

export const TreatmentsContextProvider = ({
  fileId,
  children,
  isViewable = true,
  isViewInQuickbooksEnabled = true,
  treatmentEvents = null,
}: TreatmentsContextProviderProps) => {
  const storeRef = useRef<StoreApi<TreatmentZustandStore>>();
  if (!storeRef.current) {
    storeRef.current = createTreatmentStore({
      automation: localStorage.getItem(`${FILE_AUTOMATION_LOCAL_STORAGE_KEY}-${fileId}`),
      fileId,
    });
  }
  const store = useStore(storeRef.current);

  const handleOnPublished = useCallback(() => {
    treatmentEvents?.onTreatmentPublished();
    store.onReset();
  }, [treatmentEvents, store]);

  const handleInitializeAutomatedDraft = useCallback(
    ({ fileId, draft, form }: InitializeDraftAutomationDTO) => {
      store.initializeDraftFromAutomation({ fileId, draft, form });
      treatmentEvents?.onDraftAction();
    },
    [treatmentEvents, store]
  );

  return (
    <TreatmentsContext.Provider
      value={{
        ...store,
        isViewable,
        isViewInQuickbooksEnabled,
        onPublished: handleOnPublished,
        onDraftAction: treatmentEvents?.onDraftAction,
        initializeDraftFromAutomation: handleInitializeAutomatedDraft,
      }}
    >
      {children}
    </TreatmentsContext.Provider>
  );
};

export const useTreatments = (): TreatmentsStore => {
  const context = useContext(TreatmentsContext);
  if (!context) {
    throw new Error('useTreatments must be used within a TreatmentsContextProvider');
  }
  return context;
};
